/**
 * aggregateSeries (#2005) — the "Alle Konten" total (D1). The twin has NO
 * tenant-total series server-side (the worker materialiser is per-account grain),
 * so the total is composed client-side: run ``computeBalanceSeries`` once per
 * account (each against ITS own anchor + limit — no mixed-saldo defect) and sum
 * the per-account daily balances.
 *
 * Method: the UNION of dates with per-account carry-forward. A balance is a STOCK,
 * not a flow — so for each date ``d`` an account contributes:
 *   • 0            when ``d < account.start`` (it has not opened yet — do NOT
 *                  inject its opening balance before its anchor);
 *   • balance(d)   when ``account.start ≤ d ≤ account.end`` (it is active);
 *   • last balance when ``d > account.end`` (its series ends before the union
 *                  horizon — its money persists, so carry the last known balance).
 *
 * This replaces an earlier date-INTERSECTION sum, which blanked the total for
 * staggered anchors (codex P1 on #2037): an account anchored later (a real case —
 * a bank opened mid-year, or a future opening_balance_date) dropped every earlier
 * date, and a far-future anchor made the intersection empty even though other
 * accounts held real liquidity. The union+carry-forward is a strict superset —
 * fully-overlapping anchors still sum correctly per date.
 *
 * The per-date VALUES and the ``isActual`` LABEL are decided separately: values
 * always carry forward per account (the ``last`` field below), while the label
 * comes from a single farm-wide boundary — see ``renderedIstHorizon`` for that
 * rule, for why only accounts with post-anchor actuals get a vote in it, and for
 * why the conservative boundary lives on beside it as ``verdictHorizon``.
 *
 * Pure — no React/network; recomputed per render like the rest of the surface.
 */
import { addDays, buildVerdict } from './computeBalanceSeries';
import type { BalanceSeriesResult, DailyBalancePoint } from './types';

const EMPTY: BalanceSeriesResult = {
  dailySeries: [],
  verdict: buildVerdict([], null),
  firstProblemMonth: null,
  istHorizon: '',
  istHorizonAdvanced: false,
  verdictHorizon: '',
  statementResiduals: [],
  reconciled: null,
};

export function sumBalanceSeries(perAccount: BalanceSeriesResult[]): BalanceSeriesResult {
  // Only accounts that actually projected a curve contribute (an empty series has
  // no dates to union and holds no stock).
  const contributing = perAccount.filter((s) => s.dailySeries.length > 0);
  if (contributing.length === 0) return EMPTY;

  // Per-account O(1) date → balance lookup, the account's [start, end], and the
  // last-known balance to carry forward once the account's own series ends.
  const maps = contributing.map((s) => {
    const byDate = new Map<string, number>();
    for (const p of s.dailySeries) byDate.set(p.date, p.balanceEur);
    return {
      byDate,
      start: s.dailySeries[0].date,
      end: s.dailySeries[s.dailySeries.length - 1].date,
      last: s.dailySeries[s.dailySeries.length - 1].balanceEur,
    };
  });

  // Union window = [min start, max end] — contiguous, no gaps.
  const start = maps.reduce((mn, m) => (m.start < mn ? m.start : mn), maps[0].start);
  const end = maps.reduce((mx, m) => (m.end > mx ? m.end : mx), maps[0].end);

  // The RENDERED Ist/Prognose boundary: the LATEST horizon among the accounts
  // that actually carry post-anchor actuals (HOF-5, narrowing pool from #3241).
  // See ``renderedIstHorizon`` below — the pool filter is what keeps this honest,
  // and the conservative boundary survives untouched as ``verdictHorizon``.
  const istHorizon = renderedIstHorizon(contributing);

  const dailySeries: DailyBalancePoint[] = [];
  for (let d = start; d <= end; d = addDays(d, 1)) {
    let sum = 0;
    for (const m of maps) {
      if (d < m.start) continue; // not yet opened → holds nothing
      else if (d > m.end) sum += m.last; // ended → its stock persists (carry forward)
      else sum += m.byDate.get(d) ?? 0; // active → its balance that day
    }
    dailySeries.push({
      date: d,
      balanceEur: Math.round(sum * 100) / 100,
      isActual: istHorizon !== '' && d <= istHorizon,
    });
  }

  // Σ known KK-limits (null→0); all-null → null (a neutral aggregate verdict).
  const limits = contributing.map((s) => s.verdict.kkLimitEur);
  const totalLimit = limits.every((l) => l == null)
    ? null
    : limits.reduce<number>((acc, l) => acc + (l ?? 0), 0);

  // The Ampel is judged from the CONSERVATIVE boundary, not the rendered one
  // (codex r1 P1 on #3241): everything past the strict MIN counts as forecast,
  // including the days #3241 relabelled as Ist. Otherwise a dormant account's
  // planned outflow landing inside the widened Ist window would be dropped from
  // ``projection`` and its Engpass would never reach the verdict.
  const verdictHorizon = strictMinVerdictHorizon(contributing);
  const projection = dailySeries.filter((p) => verdictHorizon === '' || p.date > verdictHorizon);
  const verdict = buildVerdict(projection, totalLimit);
  return {
    dailySeries,
    verdict,
    firstProblemMonth: verdict.firstProblemYm,
    istHorizon,
    // The total is "advanced" iff at least one account backed it with real
    // post-anchor actuals — keeps the flag honest if a total is ever re-summed.
    istHorizonAdvanced: contributing.some((s) => s.istHorizonAdvanced),
    // Published so every downstream risk consumer (the header breach/overrun in
    // ``summary.ts``, and the farmer-facing "Engpass ab …" headline it feeds)
    // judges from the same conservative boundary the verdict above used.
    verdictHorizon,
    // HOF-97 §6 L5.3/L5.4 — the total's reconciliation, folded from its parts.
    // "Alle Konten" is the exact tile whose number the customer disputed, so it is
    // the one that most needs to be able to say "this does not tie out to the bank".
    // Each residual carries the Konto it came from (codex round 2, P2 — the backend
    // aggregation already did this and the type contract already promised it). A residual
    // that cannot name its account measures nothing an operator can act on, which is the
    // whole reason the number is published.
    statementResiduals: contributing.flatMap((s) =>
      s.statementResiduals.map((r) => (s.accountId ? { ...r, accountId: s.accountId } : r)),
    ),
    // Order matters: a KNOWN drift outranks an unknown, so one measured residual makes
    // the whole total ``false`` even if nine other Konten were never checked. The
    // reverse fold would let an unchecked account launder a measured defect into
    // "unknown" — which is the exact conflation this issue exists to remove.
    reconciled: contributing.some((s) => s.reconciled === false)
      ? false
      : contributing.some((s) => s.reconciled === null)
        ? null
        : true,
  };
}

/**
 * The farm-wide RENDERED Ist/Prognose boundary: the LATEST horizon across the
 * accounts that actually carry post-anchor actuals.
 *
 * ⚠️ The ``istHorizonAdvanced`` filter is not decoration — read on before
 * touching either half of this rule.
 *
 * **History, because this line has now moved twice.** It began as a strict MIN
 * over every contributing account: a total day was ACTUAL only while EVERY
 * account was still in its confirmed region. That was deliberately conservative
 * and load-bearing for **#2611** (P1, WF, 2026-07-31), where a silent 10.000-row
 * pagination cap (MAX_PAGES 10 × PAGE_LIMIT 1000) truncated a bank feed and made
 * one account's ``istHorizon`` REGRESS far below reality; the strict MIN turned
 * that into a visible signal (the whole curve dashed back) instead of a silently
 * wrong number. #3241 then narrowed the MIN's pool to accounts that had advanced
 * past their own anchor, because a junk ``financial_account`` — synthesised from
 * a supplier e-mail, holding one bank fact dated BEFORE its own
 * ``opening_balance_date`` — pinned the boundary to 21.08.2025 while 10 of 12
 * real Konten carried actuals into Jun/Jul/Aug 2026.
 *
 * **HOF-5 finished the job the #3241 narrowing started.** Excluding junk
 * accounts was never the whole defect: ANY one Konto whose Kontoauszug ends
 * earlier drags the entire farm's rendered boundary back with it. On WF that
 * shipped as „Ist bis 30.06. · Prognose ab 01.07." while bank facts ran to
 * 30.07 — a month of real, confirmed bookings across every other Konto drawn as
 * forecast. A farmer reading that curve is being told the farm's money is
 * unconfirmed when it is banked. So the rendered boundary now answers the
 * question it is actually asked — *how current is this farm's bank data?* — with
 * the farm's latest confirmed booking.
 *
 * **#2611's signal is not deleted, it MOVED — and it moved somewhere louder.**
 * ``verdictHorizon`` still carries the untouched strict MIN over every
 * contributing account, so a lagging or truncated feed is fully recoverable:
 * ``verdictHorizon < istHorizon`` IS the drift, and the page renders it as a
 * named clause („… · einzelne Konten nur bis 30.06.") beside the caption. That
 * is a strictly better signal than the one it replaces — a dashed line says
 * *something* is unconfirmed without ever saying what, and on WF it said it
 * about eleven Konten that were fine. Every consumer that must not over-state
 * confirmation already reads ``verdictHorizon`` rather than this value: the
 * Ampel (``strictMinVerdictHorizon`` below), the did-not-happen grading
 * (``buildLedgerRows``), the breach scan (``summary.ts``) and the phantom-row
 * bound (``deriveDateBounds``). This value is the LABEL, and only the label.
 *
 * **Why the pool filter is now MORE load-bearing, not less.** Under a MIN, an
 * account with a FUTURE ``opening_balance_date`` was harmless — its late horizon
 * could never win a minimum. Under a MAX it would win outright and paint weeks
 * of pure future as confirmed Ist. ``istHorizonAdvanced`` is what rules that out:
 * such an account's horizon is its own anchor echoed back (``computeBalanceSeries``
 * caps at ``today`` then floors back up to the anchor), which is exactly what
 * "not advanced" means, so it never enters the pool. Removing the filter here
 * reopens #3241 r3 P2 immediately — pinned by the future-anchor tests on both
 * sides of the port.
 *
 * Known, accepted residual (widened from #3241's, knowingly): a day inside the
 * boundary can include a lagging account's carry-forward/projection, so "Ist"
 * means "the farm's bank data is confirmed through here", not "every euro on
 * this day is confirmed". The latter is what ``verdictHorizon`` means, it is
 * what the money-touching consumers use, and the gap between the two is
 * disclosed rather than hidden.
 */
function renderedIstHorizon(series: BalanceSeriesResult[]): string {
  // Only accounts with post-anchor actuals get a vote — see the filter rationale
  // above; under a MAX this is what keeps a future-anchored account out. If NONE
  // has any (a fresh tenant carrying only opening balances), the pool would be
  // empty and the boundary would blank, dashing the entire chart — so fall back
  // to the strict MIN over everything, which is also what stops an all-dormant
  // farm from rendering its furthest-future anchor as confirmed.
  const advanced = series.filter((s) => s.istHorizonAdvanced);
  if (advanced.length === 0) return minOf(series.map((s) => s.istHorizon));
  return maxOf(advanced.map((s) => s.istHorizon));
}

/**
 * The pre-#3241 rule: MIN across EVERY contributing account, no exclusions.
 *
 * The boundary the VERDICT is judged from (codex r1 P1 on #3241), and — since
 * HOF-5 — the farm's conservative "every euro confirmed through here" answer,
 * which the page also renders as the lag disclosure whenever it sits behind the
 * label. The rendered boundary widens past a lagging or dormant account's own
 * horizon, but that account's curve is still summed into the total — so judging
 * the Ampel off the widened boundary would drop those days from ``projection``
 * and hide a real forecast Engpass. Because this is a MIN over the WHOLE set it
 * is always ≤ ``renderedIstHorizon``, so the verdict always sees a superset of
 * the forecast days: the Ampel can never become less sensitive than it was.
 *
 * Reads each input's own JUDGED boundary rather than its rendered one. Identical
 * for the per-account inputs this is always called with (equal by construction),
 * but it keeps the conservative boundary intact rather than silently discarding
 * it if an aggregate were ever re-summed.
 */
function strictMinVerdictHorizon(series: BalanceSeriesResult[]): string {
  return minOf(series.map((s) => s.verdictHorizon));
}

/** Earliest non-empty ISO date, or '' when there is none. */
function minOf(horizons: string[]): string {
  const present = horizons.filter((h) => h !== '');
  if (present.length === 0) return '';
  return present.reduce((mn, h) => (h < mn ? h : mn), present[0]);
}

/** Latest non-empty ISO date, or '' when there is none. */
function maxOf(horizons: string[]): string {
  const present = horizons.filter((h) => h !== '');
  if (present.length === 0) return '';
  return present.reduce((mx, h) => (h > mx ? h : mx), present[0]);
}
